import { ApiResponse, UserRole } from '@shared/types';
import {
  CreateSalesClientInput,
  DiscountInputType,
  OrderDiscount,
  OrderKotConfirmation,
  OrderMode,
  OrderPaymentMethod,
  OrderSettlement,
  OrderWaiter,
  OtherSettlementType,
  SalesClient,
  SalesOrder,
  SalesOrderItem,
  SplitSettlementPaymentInput,
  UpdateSalesClientInput,
} from '../types/order';
import { supabase } from './supabase';

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const mapOrder = (order: any): SalesOrder => ({
  order_id: order.order_id,
  bill_number: toNumber(order.bill_number),
  table_id: toNumber(order.table_id),
  table_name: order.table_name || '',
  section_id: toNumber(order.section_id),
  section_name: order.section_name || '',
  outlet_id: order.outlet_id,
  outlet_name: order.outlet_name || '',
  company_id: toNumber(order.company_id),
  date: order.date,
  status_id: toNumber(order.status_id),
  status_name: order.status_name || '',
  waiter_id: order.waiter_id,
  waiter_name: order.waiter_name || '',
  client_id: order.client_id ?? null,
  client_name: order.client_name ?? null,
  mode: order.mode,
  discount_amount: toNumber(order.discount_amount),
  total_amount: toNumber(order.total_amount),
  net_amount: toNumber(order.net_amount),
  item_count: toNumber(order.item_count),
  pending_item_count: toNumber(order.pending_item_count),
  has_pending_items: Boolean(order.has_pending_items),
  created_by: order.created_by,
  created_at: order.created_at,
  updated_at: order.updated_at,
  creator_name: order.creator_name ?? null,
  creator_email: order.creator_email ?? null,
  discount_count: toNumber(order.discount_count),
  last_discount_at: order.last_discount_at ?? null,
  settlement_total: toNumber(order.settlement_total),
  settlement_count: toNumber(order.settlement_count),
  settlement_methods: Array.isArray(order.settlement_methods)
    ? order.settlement_methods
    : [],
  last_settlement_at: order.last_settlement_at ?? null,
  balance_amount: toNumber(order.balance_amount),
  is_fully_settled: Boolean(order.is_fully_settled),
});

const mapOrderItem = (item: any): SalesOrderItem => {
  const departmentData = Array.isArray(item.menu_items?.departments)
    ? item.menu_items.departments[0]
    : item.menu_items?.departments;
  const addedByUser = Array.isArray(item.users) ? item.users[0] : item.users;
  const addedByName = addedByUser
    ? `${addedByUser.first_name || ''} ${addedByUser.last_name || ''}`.trim()
    : null;

  return {
    id: item.id,
    order_id: item.order_id,
    menu_item_id: item.menu_item_id,
    name: item.menu_items?.name || '',
    department_id: item.menu_items?.department_id ?? null,
    department_name: departmentData?.name ?? null,
    quantity: toNumber(item.quantity),
    unit_price: toNumber(item.unit_price),
    amount: toNumber(item.amount),
    notes: item.notes ?? null,
    status: item.status,
    kot_id: toNumber(item.kot_id),
    added_by: item.added_by ?? null,
    added_by_name: addedByName || null,
    created_at: item.created_at,
    updated_at: item.updated_at,
  };
};

const mapOrderDiscount = (discount: any): OrderDiscount => ({
  id: discount.id,
  order_id: discount.order_id,
  amount: toNumber(discount.amount),
  added_by: discount.added_by,
  notes: discount.notes ?? null,
  created_at: discount.created_at,
  updated_at: discount.updated_at,
});

const mapOrderPaymentMethod = (paymentMethod: any): OrderPaymentMethod => ({
  id: toNumber(paymentMethod.id),
  name: paymentMethod.name || '',
});

const mapSalesClient = (client: any): SalesClient => ({
  id: client.id,
  first_name: client.first_name || '',
  last_name: client.last_name || '',
  full_name: `${client.first_name || ''} ${client.last_name || ''}`.trim(),
  email: client.email ?? null,
  contact: client.contact ?? null,
  address: client.address ?? null,
  tin_number: client.tin_number ?? null,
  menu_percentage_rate: toNumber(client.menu_percentage_rate),
});

const mapOrderSettlement = (settlement: any): OrderSettlement => ({
  id: settlement.id,
  order_id: settlement.order_id,
  amount: toNumber(settlement.amount),
  payment_method_id: toNumber(settlement.payment_method_id),
  notes: settlement.notes ?? null,
  created_by: settlement.created_by,
  created_at: settlement.created_at,
});

const mapOrderWaiter = (waiter: any): OrderWaiter => ({
  id: waiter.id,
  first_name: waiter.first_name || '',
  last_name: waiter.last_name || '',
  full_name: `${waiter.first_name || ''} ${waiter.last_name || ''}`.trim(),
  email: waiter.email ?? null,
  role: waiter.role || UserRole.WAITER,
  outlet_id: waiter.outlet_id ?? null,
});

const mapOrderKotConfirmation = (confirmation: any): OrderKotConfirmation => ({
  kot_id: toNumber(confirmation.kot_id),
  confirmed_count: toNumber(confirmation.confirmed_count),
});

export const getOrCreateTableOrder = async (
  tableId: number,
  mode: OrderMode = 'DINE',
): Promise<ApiResponse<SalesOrder>> => {
  try {
    const { data, error } = await supabase.rpc('get_or_create_table_order', {
      p_table_id: tableId,
      p_mode: mode,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const order = Array.isArray(data) ? data[0] : data;
    if (!order) {
      return {
        success: false,
        error: 'No order was returned',
        data: undefined,
      };
    }

    return { success: true, data: mapOrder(order) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to open the selected table order',
      data: undefined,
    };
  }
};

export const fetchRunningTableOrder = async (
  tableId: number,
): Promise<ApiResponse<SalesOrder | null>> => {
  try {
    const { data, error } = await supabase
      .from('orders_view')
      .select('*')
      .eq('table_id', tableId)
      .eq('status_name', 'OPEN')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    const order = (data || [])[0];
    if (!order) {
      return { success: true, data: null };
    }

    return { success: true, data: mapOrder(order) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch table running order',
      data: null,
    };
  }
};

export const fetchOpenTakeAwayOrders = async (
  outletId: string,
): Promise<ApiResponse<SalesOrder[]>> => {
  try {
    const { data, error } = await supabase
      .from('orders_view')
      .select('*')
      .eq('outlet_id', outletId)
      .eq('status_name', 'OPEN')
      .in('mode', ['TAKEAWAY', 'CASH'])
      .order('created_at', { ascending: false });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((row: any) => mapOrder(row)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch takeaway orders',
      data: [],
    };
  }
};

export const createTakeAwayOrder = async (
  outletId?: string,
): Promise<ApiResponse<SalesOrder>> => {
  try {
    const { data, error } = await supabase.rpc('create_takeaway_order', {
      p_outlet_id: outletId ?? null,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const order = Array.isArray(data) ? data[0] : data;
    if (!order) {
      return {
        success: false,
        error: 'No takeaway order was returned',
        data: undefined,
      };
    }

    return { success: true, data: mapOrder(order) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to create takeaway order',
      data: undefined,
    };
  }
};

export const fetchOrderById = async (
  orderId: string,
): Promise<ApiResponse<SalesOrder>> => {
  try {
    const { data, error } = await supabase
      .from('orders_view')
      .select('*')
      .eq('order_id', orderId)
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapOrder(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch order details',
      data: undefined,
    };
  }
};

export const fetchOrderItems = async (
  orderId: string,
): Promise<ApiResponse<SalesOrderItem[]>> => {
  try {
    const { data, error } = await supabase
      .from('order_items')
      .select(
        'id, order_id, menu_item_id, quantity, unit_price, amount, notes, status, kot_id, added_by, created_at, updated_at, menu_items(name, department_id, departments(name)), users(first_name, last_name)',
      )
      .eq('order_id', orderId)
      .order('created_at', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item: any) => mapOrderItem(item)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch order items',
      data: [],
    };
  }
};

export const addOrderItem = async (
  orderId: string,
  menuItemId: string,
  quantity: number,
  unitPrice: number,
  notes?: string,
): Promise<ApiResponse<SalesOrderItem>> => {
  try {
    const { data, error } = await supabase
      .from('order_items')
      .insert({
        order_id: orderId,
        menu_item_id: menuItemId,
        quantity,
        unit_price: unitPrice,
        notes: notes?.trim() || null,
      })
      .select(
        'id, order_id, menu_item_id, quantity, unit_price, amount, notes, status, kot_id, added_by, created_at, updated_at, menu_items(name, department_id, departments(name)), users(first_name, last_name)',
      )
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapOrderItem(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to add order item',
      data: undefined,
    };
  }
};

export const addOrderDiscount = async (
  orderId: string,
  value: number,
  type: DiscountInputType,
  notes?: string,
): Promise<ApiResponse<OrderDiscount>> => {
  try {
    const { data, error } = await supabase.rpc('add_order_discount', {
      p_order_id: orderId,
      p_discount_value: value,
      p_discount_type: type,
      p_notes: notes ?? null,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    if (!data) {
      return {
        success: false,
        error: 'No discount record returned',
        data: undefined,
      };
    }

    return { success: true, data: mapOrderDiscount(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to add order discount',
      data: undefined,
    };
  }
};

export const removeOrderDiscount = async (
  orderId: string,
): Promise<ApiResponse<boolean>> => {
  try {
    const { data, error } = await supabase.rpc('remove_order_discount', {
      p_order_id: orderId,
    });

    if (error) {
      return { success: false, error: error.message, data: false };
    }

    return { success: true, data: Boolean(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to remove discount',
      data: false,
    };
  }
};

export const fetchSettlementPaymentMethods = async (): Promise<
  ApiResponse<OrderPaymentMethod[]>
> => {
  try {
    const { data, error } = await supabase
      .from('payment_methods')
      .select('id, name')
      .in('name', ['CASH', 'CARD', 'MOBILE MONEY', 'CHECK', 'EFT'])
      .order('id', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((row: any) => mapOrderPaymentMethod(row)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch settlement payment methods',
      data: [],
    };
  }
};

export const fetchCompanyClients = async (opts?: {
  search?: string;
  offset?: number;
  limit?: number;
}): Promise<ApiResponse<{ clients: SalesClient[]; count: number }>> => {
  const { search, offset = 0, limit } = opts ?? {};
  try {
    let query = supabase
      .from('clients')
      .select(
        'id, first_name, last_name, email, contact, address, tin_number, menu_percentage_rate',
      )
      .order('first_name', { ascending: true });

    if (search?.trim()) {
      const term = search.trim();
      query = query.or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%`);
    }

    if (limit !== undefined) {
      query = query.range(offset, offset + limit - 1);
    }

    const { data, error } = await query;

    // getTotal count
    const { count, error: countError } = await supabase
      .from('clients')
      .select('*', { count: 'exact', head: true });

    if (error || countError) {
      return {
        success: false,
        error: error?.message,
        data: { clients: [], count: 0 },
      };
    }

    return {
      success: true,
      data: {
        clients: (data || []).map((row: any) => mapSalesClient(row)),
        count: count || 0,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch company clients',
      data: { clients: [], count: 0 },
    };
  }
};

export const fetchOutletWaiters = async (
  outletId: string,
): Promise<ApiResponse<OrderWaiter[]>> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, first_name, last_name, email, role, outlet_id')
      .eq('outlet_id', outletId)
      .eq('role', UserRole.WAITER)
      .order('first_name', { ascending: true })
      .order('last_name', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((row: any) => mapOrderWaiter(row)),
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch waiters',
      data: [],
    };
  }
};

export const updateOrderWaiter = async (
  orderId: string,
  waiterId: string,
): Promise<ApiResponse<boolean>> => {
  try {
    const { error } = await supabase
      .from('orders')
      .update({ waiter_id: waiterId, updated_at: new Date().toISOString() })
      .eq('id', orderId);

    if (error) {
      return { success: false, error: error.message, data: false };
    }

    return { success: true, data: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update waiter',
      data: false,
    };
  }
};

export const assignClientToOrder = async (
  orderId: string,
  clientId: string,
): Promise<ApiResponse<boolean>> => {
  try {
    const { error } = await supabase
      .from('orders')
      .update({ client_id: clientId, updated_at: new Date().toISOString() })
      .eq('id', orderId);

    if (error) {
      return { success: false, error: error.message, data: false };
    }

    return { success: true, data: true };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to add client to order',
      data: false,
    };
  }
};

export const createCompanyClient = async (
  payload: CreateSalesClientInput,
): Promise<ApiResponse<SalesClient>> => {
  try {
    // eslint-disable-next-line no-console
    console.log('Creating new client:::', payload);
    const { data, error } = await supabase
      .from('clients')
      .insert({
        company_id: payload.company_id,
        first_name: payload.first_name.trim(),
        last_name: payload.last_name.trim(),
        email: payload.email?.trim() || null,
        contact: payload.contact?.trim() || null,
        address: payload.address?.trim() || null,
        tin_number: payload.tin_number?.trim() || null,
      })
      .select(
        'id, first_name, last_name, email, contact, address, tin_number, menu_percentage_rate',
      )
      .single();

    if (error) {
      // eslint-disable-next-line no-console
      console.log('Error creating client:::', error);
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapSalesClient(data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create client',
      data: undefined,
    };
  }
};

export const updateCompanyClient = async (
  payload: UpdateSalesClientInput,
): Promise<ApiResponse<SalesClient>> => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .update({
        first_name: payload.first_name.trim(),
        last_name: payload.last_name.trim(),
        email: payload.email?.trim() || null,
        contact: payload.contact?.trim() || null,
        address: payload.address?.trim() || null,
        tin_number: payload.tin_number?.trim() || null,
        ...(payload.menu_percentage_rate !== undefined
          ? { menu_percentage_rate: payload.menu_percentage_rate }
          : {}),
      })
      .eq('id', payload.id)
      .select(
        'id, first_name, last_name, email, contact, address, tin_number, menu_percentage_rate',
      )
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapSalesClient(data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update client',
      data: undefined,
    };
  }
};

export const directSettleOrder = async (
  orderId: string,
  paymentMethodId: number,
  notes?: string,
): Promise<ApiResponse<OrderSettlement>> => {
  try {
    const { data, error } = await supabase.rpc('direct_settlement', {
      p_order_id: orderId,
      p_payment_method_id: paymentMethodId,
      p_notes: notes?.trim() || null,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    if (!data) {
      return {
        success: false,
        error: 'No settlement record returned',
        data: undefined,
      };
    }

    return { success: true, data: mapOrderSettlement(data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to settle order',
      data: undefined,
    };
  }
};

export const splitSettleOrder = async (
  orderId: string,
  payments: SplitSettlementPaymentInput[],
  notes?: string,
): Promise<ApiResponse<OrderSettlement[]>> => {
  try {
    const payload = payments.map((payment) => ({
      payment_method_id: payment.payment_method_id,
      amount: Number(payment.amount),
    }));

    const { data, error } = await supabase.rpc('split_settlement', {
      p_order_id: orderId,
      p_payments: payload,
      p_notes: notes?.trim() || null,
    });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (Array.isArray(data) ? data : []).map((row: any) =>
        mapOrderSettlement(row),
      ),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to submit split settlement',
      data: [],
    };
  }
};

export const companySettleOrder = async (
  orderId: string,
  clientId: string,
  notes?: string,
): Promise<ApiResponse<OrderSettlement>> => {
  try {
    const { data, error } = await supabase.rpc('company_settlement', {
      p_order_id: orderId,
      p_client_id: clientId,
      p_notes: notes?.trim() || null,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    if (!data) {
      return {
        success: false,
        error: 'No settlement record returned',
        data: undefined,
      };
    }

    return { success: true, data: mapOrderSettlement(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save credit settlement',
      data: undefined,
    };
  }
};

export const settleCreditBill = async (
  orderId: string,
  paymentMethodId: number,
  notes?: string,
): Promise<ApiResponse<OrderSettlement>> => {
  try {
    const { data, error } = await supabase.rpc('settle_credit_bill', {
      p_order_id: orderId,
      p_payment_method_id: paymentMethodId,
      p_notes: notes?.trim() || null,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    if (!data) {
      return {
        success: false,
        error: 'No settlement record returned',
        data: undefined,
      };
    }

    return { success: true, data: mapOrderSettlement(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to settle credit bill',
      data: undefined,
    };
  }
};

export const otherSettleOrder = async (
  orderId: string,
  settlementType: OtherSettlementType,
  notes?: string,
): Promise<ApiResponse<OrderSettlement>> => {
  try {
    const { data, error } = await supabase.rpc('other_settlement', {
      p_order_id: orderId,
      p_settlement_type: settlementType,
      p_notes: notes?.trim() || null,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    if (!data) {
      return {
        success: false,
        error: 'No settlement record returned',
        data: undefined,
      };
    }

    return { success: true, data: mapOrderSettlement(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to save settlement',
      data: undefined,
    };
  }
};

export const confirmOrderKot = async (
  orderId: string,
): Promise<ApiResponse<OrderKotConfirmation>> => {
  try {
    const { data, error } = await supabase.rpc('confirm_order_kot', {
      p_order_id: orderId,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const confirmation = Array.isArray(data) ? data[0] : data;
    if (!confirmation) {
      return {
        success: false,
        error: 'No KOT confirmation payload returned',
        data: undefined,
      };
    }

    return { success: true, data: mapOrderKotConfirmation(confirmation) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to confirm KOT',
      data: undefined,
    };
  }
};

export const updatePendingOrderItemNotes = async (
  orderItemId: string,
  notes: string,
): Promise<ApiResponse<SalesOrderItem>> => {
  try {
    const { data, error } = await supabase.rpc(
      'update_pending_order_item_notes',
      {
        p_order_item_id: orderItemId,
        p_notes: notes?.trim() || null,
      },
    );

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    if (!data) {
      return {
        success: false,
        error: 'No updated order item was returned',
        data: undefined,
      };
    }

    return { success: true, data: mapOrderItem(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to update order item notes',
      data: undefined,
    };
  }
};

export const removeOrderItem = async (
  orderItemId: string,
  reason?: string,
): Promise<ApiResponse<SalesOrderItem>> => {
  try {
    const { data, error } = await supabase.rpc('remove_order_item', {
      p_order_item_id: orderItemId,
      p_reason: reason?.trim() || null,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    if (!data) {
      return {
        success: false,
        error: 'No removed order item was returned',
        data: undefined,
      };
    }

    return { success: true, data: mapOrderItem(data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to remove item',
      data: undefined,
    };
  }
};

export const shiftConfirmedOrderItemToTable = async (
  orderItemId: string,
  targetTableId: number,
): Promise<ApiResponse<boolean>> => {
  try {
    const { data, error } = await supabase.rpc(
      'shift_confirmed_order_item_to_table',
      {
        p_order_item_id: orderItemId,
        p_target_table_id: targetTableId,
      },
    );

    if (error) {
      return { success: false, error: error.message, data: false };
    }

    return { success: true, data: Boolean(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to shift order item',
      data: false,
    };
  }
};

export const shiftConfirmedOrderItemsToTable = async (
  orderItemIds: string[],
  targetTableId: number,
): Promise<ApiResponse<number>> => {
  try {
    const { data, error } = await supabase.rpc(
      'shift_confirmed_order_items_to_table',
      {
        p_order_item_ids: orderItemIds,
        p_target_table_id: targetTableId,
      },
    );

    if (error) {
      return { success: false, error: error.message, data: 0 };
    }

    return { success: true, data: Number(data) || 0 };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to shift order items',
      data: 0,
    };
  }
};

export const shiftOrderToTable = async (
  orderId: string,
  targetTableId: number,
): Promise<ApiResponse<boolean>> => {
  try {
    const { data, error } = await supabase.rpc('shift_order_to_table', {
      p_order_id: orderId,
      p_target_table_id: targetTableId,
    });

    if (error) {
      return { success: false, error: error.message, data: false };
    }

    return { success: true, data: Boolean(data) };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to shift order',
      data: false,
    };
  }
};

export const sendCreditSettlementEmail = async (
  orderId: string,
  outletId: string,
): Promise<void> => {
  const { error } = await supabase.functions.invoke(
    'send-credit-settlement-email',
    { body: { order_id: orderId, outlet_id: outletId } },
  );
  if (error)
    throw new Error(error.message ?? 'Failed to send credit settlement email');
};
